import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { authService } from '../services/authService';
import { userManagementService } from '../services/adminService';
import { clearAccessCache as clearCachedAccess, getStoredAccessSnapshot } from '../services/accessService';
import { clearE2eRuntime, isE2eMocksEnabled, readE2eAccess, readE2eSession } from '../testing/e2eRuntime';

const AuthContext = createContext(null);

const EMPTY_ACCESS = {
	userId: null,
	tenantId: null,
	roleId: null,
	roleName: null,
	permissions: [],
};

const normalizeAccess = (payload = {}) => ({
	userId: payload.user_id || null,
	tenantId: payload.tenant_id || null,
	roleId: payload.role_id || null,
	roleName: payload.role || null,
	permissions: payload.permissions instanceof Set
		? Array.from(payload.permissions)
		: Array.isArray(payload.permissions)
			? payload.permissions
			: [],
});

const hasUsableAccess = (payload = EMPTY_ACCESS) => Boolean(payload?.userId && payload?.tenantId);

function readInitialState() {
	if (isE2eMocksEnabled()) {
		const e2eSession = readE2eSession();
		const e2eAccess = readE2eAccess();
		return {
			user: e2eSession?.user || null,
			session: e2eSession || null,
			isAdmin: e2eSession?.user?.app_metadata?.role === 'admin',
			access: normalizeAccess(e2eAccess || EMPTY_ACCESS),
			initializing: !e2eSession,
			accessLoading: !e2eAccess,
		};
	}

	return {
		user: null,
		session: null,
		isAdmin: false,
		access: EMPTY_ACCESS,
		initializing: true,
		accessLoading: true,
	};
}

export const AuthProvider = ({ children }) => {
	const [initialState] = useState(() => readInitialState());
	const [user, setUser] = useState(initialState.user);
	const [session, setSession] = useState(initialState.session);
	const [isAdmin, setIsAdmin] = useState(initialState.isAdmin);
	const [initializing, setInitializing] = useState(initialState.initializing);
	const [access, setAccess] = useState(initialState.access);
	const [accessLoading, setAccessLoading] = useState(initialState.accessLoading);
	const [accessRefreshing, setAccessRefreshing] = useState(false);
	const [accessError, setAccessError] = useState(null);
	const accessRequestIdRef = useRef(0);
	const accessRef = useRef(initialState.access);

	const applyAccessState = useCallback((payload = EMPTY_ACCESS) => {
		const normalized = normalizeAccess(payload);
		accessRef.current = normalized;
		setAccess(normalized);
		return normalized;
	}, []);

	const applySession = useCallback((nextSession) => {
		clearCachedAccess();
		setSession(nextSession);
		setUser(nextSession?.user || null);
		setIsAdmin(nextSession?.user?.app_metadata?.role === 'admin');
	}, []);

	const clearAccess = useCallback(({ clearPersisted = true } = {}) => {
		accessRequestIdRef.current += 1;
		clearCachedAccess({ clearPersisted });
		applyAccessState(EMPTY_ACCESS);
		setAccessError(null);
		setAccessLoading(false);
		setAccessRefreshing(false);
	}, [applyAccessState]);

	const refreshAccess = useCallback(async ({ background = false } = {}) => {
		if (!session?.access_token || !user?.id) {
			clearAccess();
			return { access: null, status: null, error: null };
		}

		const requestId = ++accessRequestIdRef.current;
		if (background) {
			setAccessRefreshing(true);
		} else {
			setAccessLoading(true);
			setAccessRefreshing(false);
		}

		try {
			const result = await userManagementService.getCurrentUserAccess();

			if (requestId !== accessRequestIdRef.current) {
				return result;
			}

			if (result.error || !result.access) {
				const isPermissionFailure = [401, 403].includes(Number(result.status));
				const keepHydratedAccess = background && hasUsableAccess(accessRef.current) && !isPermissionFailure;
				if (!keepHydratedAccess) {
					clearCachedAccess({ clearPersisted: true });
					applyAccessState(EMPTY_ACCESS);
				}
				setAccessError(result.error || 'Failed to load current user access');
			} else {
				applyAccessState(result.access);
				setAccessError(null);
			}

			return result;
		} finally {
			if (requestId === accessRequestIdRef.current) {
				setAccessLoading(false);
				setAccessRefreshing(false);
			}
		}
	}, [applyAccessState, clearAccess, session?.access_token, user?.id]);

	useEffect(() => {
		let mounted = true;

		const initializeAuth = async () => {
			if (isE2eMocksEnabled()) {
				const e2eSession = readE2eSession();
				if (e2eSession) {
					applySession(e2eSession);
					const e2eAccess = readE2eAccess();
					if (e2eAccess) {
						applyAccessState(e2eAccess);
						setAccessError(null);
					} else {
						applyAccessState(EMPTY_ACCESS);
						setAccessError(null);
					}
					setAccessLoading(false);
					setAccessRefreshing(false);
					setInitializing(false);
					return;
				}
			}

			try {
				const { session: currentSession } = await authService.getSession();
				if (!mounted) return;

				if (currentSession) {
					applySession(currentSession);
				} else {
					setSession(null);
					setUser(null);
					setIsAdmin(false);
					clearAccess();
				}
			} catch (error) {
				console.error('Error initializing auth:', error);
				if (mounted) {
					clearAccess();
				}
			} finally {
				if (mounted) {
					setInitializing(false);
				}
			}
		};

		initializeAuth();

		if (isE2eMocksEnabled() && readE2eSession()) {
			return () => {
				mounted = false;
			};
		}

		const subscription = authService.onAuthStateChange((event, nextSession) => {
			if (nextSession) {
				applySession(nextSession);
			} else {
				setSession(null);
				setUser(null);
				setIsAdmin(false);
				clearAccess();
			}
		});

		return () => {
			mounted = false;
			subscription?.unsubscribe();
		};
	}, [applyAccessState, applySession, clearAccess]);

	useEffect(() => {
		if (!session?.access_token || !user?.id) {
			if (!initializing) {
				clearAccess();
			}
			return;
		}

		if (isE2eMocksEnabled()) {
			const e2eSession = readE2eSession();
			if (e2eSession?.access_token && e2eSession.access_token === session.access_token) {
				const e2eAccess = readE2eAccess();
				applyAccessState(e2eAccess || EMPTY_ACCESS);
				setAccessError(null);
				setAccessLoading(false);
				setAccessRefreshing(false);
				return;
			}
		}

		const storedAccess = getStoredAccessSnapshot(user.id);
		if (storedAccess) {
			applyAccessState(storedAccess);
			setAccessError(null);
			setAccessLoading(false);
		}

		refreshAccess({ background: !!storedAccess });
	}, [applyAccessState, clearAccess, initializing, refreshAccess, session?.access_token, user?.id]);

	const login = async (email, password) => {
		try {
			const result = await authService.login(email, password);

			if (result.error) {
				return { error: result.error };
			}

			setAccessLoading(true);
			setAccessRefreshing(false);
			applySession(result.session);
			return { error: null, user: result.user };
		} catch (error) {
			return { error: error.message || 'Login failed' };
		}
	};

	const logout = async () => {
		try {
			if (isE2eMocksEnabled()) {
				clearE2eRuntime();
				setUser(null);
				setSession(null);
				setIsAdmin(false);
				clearAccess();
				return { error: null };
			}

			const { error } = await authService.logout();

			if (error) {
				console.error('Logout error:', error);
			}

			setUser(null);
			setSession(null);
			setIsAdmin(false);
			clearAccess();
			return { error: null };
		} catch (error) {
			return { error: error.message || 'Logout failed' };
		}
	};

	const permissionSet = useMemo(() => new Set(access.permissions || []), [access.permissions]);

	const hasPermission = useCallback((code) => {
		if (!code) return false;
		return permissionSet.has(code);
	}, [permissionSet]);

	const hasAnyPermissions = useCallback((codes = []) => {
		if (!Array.isArray(codes) || codes.length === 0) {
			return false;
		}
		return codes.some(code => permissionSet.has(code));
	}, [permissionSet]);

	const hasAllPermissions = useCallback((codes = []) => {
		if (!Array.isArray(codes) || codes.length === 0) {
			return true;
		}
		return codes.every(code => permissionSet.has(code));
	}, [permissionSet]);

	const capabilities = useMemo(() => ({
		canReadDevices: hasAnyPermissions(['device.read', 'device.write']),
		canManageDevices: hasPermission('device.write'),
		canReadFirmwares: hasAnyPermissions(['firmware.read', 'firmware.write', 'firmware.sign']),
		canSignFirmwares: hasAnyPermissions(['firmware.sign', 'firmware.write']),
		canReadKeys: hasAnyPermissions(['key.read', 'key.write', 'key.assign', 'key.rotate']),
		canManageKeys: hasPermission('key.write'),
		canAssignKeys: hasPermission('key.assign'),
		canRotateKeys: hasPermission('key.rotate'),
		canReadNetwork: hasAnyPermissions(['device.read', 'device.write', 'repo.read', 'repo.write']),
		canManageRepos: hasPermission('repo.write'),
			canReadReports: hasPermission('report.read'),
		canReadChat: hasPermission('chat.read'),
		canReadUserManagement: hasAnyPermissions(['membership.read', 'membership.write', 'role.read', 'role.write']),
		canReadRoles: hasAnyPermissions(['role.read', 'role.write']),
		canManageRoles: hasPermission('role.write'),
		canReadMembers: hasAnyPermissions(['membership.read', 'membership.write']),
		canManageMembers: hasPermission('membership.write'),
		canReadRotations: hasAnyPermissions(['key.read', 'key.write', 'key.rotate']),
		canManageApiKeys: hasPermission('api_key.write') && ['owner', 'admin'].includes(String(access.roleName || '').toLowerCase()),
		canReadOtaBatches: hasAnyPermissions(['device.read', 'device.write', 'firmware.write', 'firmware.sign', 'report.read']),
	}), [access.roleName, hasAnyPermissions, hasPermission]);

	const value = {
		user,
		session,
		initializing,
		accessLoading,
		accessRefreshing,
		accessError,
		login,
		logout,
		refreshAccess,
		isAuthenticated: !!user && !!session,
		isAdmin,
		access,
		tenantId: access.tenantId,
		roleId: access.roleId,
		roleName: access.roleName,
		permissions: access.permissions,
		hasPermission,
		hasAnyPermissions,
		hasAllPermissions,
		capabilities,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error('useAuth must be used within an AuthProvider');
	}
	return context;
};
